import { useState, useEffect } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import { useUpdateCustomer, useIdentify } from '@hungryroot/customer';

import { CaretLeft } from '@hungryroot/icons';
import { Button, Heading, Paragraph, IconButton } from '@hungryroot/ramen';
import { PROMO_ITEMS } from '../constants';
import { PromoCard } from '../PromoCard/PromoCard';
import styles from './CyoPage.module.css';

interface Props {
  selectCyoPath: string;
  backCyoPath?: string;
  onCustomerUpdated: () => void;
  headerClassName?: string;
}

export const NO_THANKS_COPY = 'No thanks';

export function CyoPage(props: Props) {
  const [selectedPromo, setSelectedPromo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { selectCyoPath, backCyoPath, onCustomerUpdated, headerClassName } =
    props;

  const customer = useIdentify();

  const { mutate: updateCustomerMutation } = useUpdateCustomer();

  useEffect(() => {
    if (customer.customerData === undefined) {
      return;
    }
    setSelectedPromo(customer.customerData?.life_promo || '');
  }, [customer, setSelectedPromo]);

  const makeSelectionHandler = (promo: string) => (e: React.SyntheticEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSelectedPromo(promo);
    updateCustomerMutation(
      {
        customer: { life_promo: promo },
      },
      {
        onSuccess: onCustomerUpdated,
      }
    );
  };

  const shouldDisableButtons = isLoading;
  return (
    <div className={styles.container}>
      {backCyoPath && (
        <IconButton
          size="large"
          prominence="tertiary"
          icon={<CaretLeft />}
          style="muted"
          ariaLabel="Back"
          className={styles['back-button']}
          buttonType="link"
          to={backCyoPath}
        />
      )}
      <div className={clsx(styles.header, headerClassName)}>
        <Heading variant="H1Alt">Choose your free gift</Heading>
        <Paragraph className={styles.tagline}>
          Pick a free item in every order from a rotating selection of veggies,
          proteins, or sweets + snacks.
        </Paragraph>
      </div>
      <div className={styles['promo-options']}>
        {PROMO_ITEMS.map((promo) => (
          <PromoCard
            key={promo.title}
            onClick={makeSelectionHandler(promo.value)}
            isSelected={selectedPromo === promo.value}
            isDisabled={shouldDisableButtons}
            {...promo}
          />
        ))}
      </div>
      <Paragraph className={styles['choose-later']}>
        You can always change your gift later with new options in every
        delivery.
      </Paragraph>
      <Button
        prominence="secondary"
        style="default"
        size="medium"
        textPrimary={NO_THANKS_COPY}
        className={styles.cta}
        buttonType="link"
        to={selectCyoPath}
        onClick={makeSelectionHandler('')}
        disabled={shouldDisableButtons}
      />
    </div>
  );
}
